<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAppStore } from '../store'
import { getRegistrations, revokeRegistration, cancelRegistration, type Registration } from '../api'
import RegistrationForm from './RegistrationForm.vue'

/**
 * The e-mail step. Who may use QuickTide is decided in Nautin's own administration; this
 * screen only asks for the address, and the link in the mail that follows says the person
 * typing it can read that mailbox. Nothing here decides anything.
 */

const store = useAppStore()
const registrations = ref<Registration[]>([])

const licence = computed(() => store.licence)
const expires = computed(() => {
  const at = licence.value?.account?.expires
  return at ? new Date(at).toLocaleDateString(store.settings.locale, { dateStyle: 'long' }) : null
})
async function loadRegistrations() {
  registrations.value = store.registered ? await getRegistrations() : []
}
watch(() => store.registered, loadRegistrations, { immediate: true })

async function revoke(id: string) {
  await store.setLicence(await revokeRegistration(id))
  await loadRegistrations()
}

async function giveUp() {
  await store.setLicence(await cancelRegistration())
}

function when(iso: string) {
  return new Date(iso).toLocaleDateString(store.settings.locale, { dateStyle: 'short' })
}
</script>

<template>
  <h2 id="activering">{{ store.t('registration') }}</h2>

  <template v-if="store.registered">
    <p class="notice small">
      {{ licence?.account?.displayName ? store.tv('registeredTo', licence.account.displayName) : store.t('registeredPlain') }}
      <template v-if="expires"> {{ store.tv('validUntil', expires) }}</template>
      <template v-else> {{ store.t('noEndDate') }}</template>
    </p>
    <template v-if="registrations.length">
      <div class="setting"><span class="small muted">{{ store.t('yourDevices') }}</span></div>
      <div v-for="r in registrations" :key="r.id" class="setting">
        <span>
          {{ r.name || r.platform || '—' }}
          <span class="small muted"><template v-if="r.current"> · {{ store.t('thisDevice') }}</template> · {{ store.t('lastUsed') }} {{ when(r.lastSeenAt) }}</span>
        </span>
        <button class="link" @click="revoke(r.id)">{{ store.t('revoke') }}</button>
      </div>
    </template>
    <div class="setting">
      <span class="small muted"></span>
      <button class="link" @click="giveUp()">{{ store.t('cancelRegistration') }}</button>
    </div>
  </template>

  <template v-else>
    <p class="notice small" :class="{ warn: licence?.state === 'expired' }">
      <template v-if="licence?.reason === 'expired'">{{ store.t('registrationExpired') }}</template>
      <template v-else-if="licence?.reason === 'unknown'">{{ store.t('registrationUnknown') }}</template>
      <template v-else>{{ store.t('demoNotice') }}</template>
    </p>
    <RegistrationForm />
  </template>
</template>
